import { useEffect, type RefObject } from 'react'
import { useBreadcrumbTitle } from '@/src/renderer/contexts/BreadcrumbTitleContext'
import { breadcrumbTitleFor } from './useProjectHeader'

interface UsePageBreadcrumbParams {
  /** Der Seitenname, wie die Überschrift ihn zeigt. */
  title: string
  isActive: boolean
  /** Erst mit Daten steht die Überschrift — vorher gibt es nichts zu beobachten. */
  loaded: boolean
  /** Leer fällt die Überschrift weg. */
  isEmpty: boolean
  titleRef: RefObject<HTMLElement | null>
  scrollerRef: RefObject<HTMLElement | null>
}

/**
 * Der Seitenname als letzte Stufe der Breadcrumb auf **Geschafft**, **Ruhend** und
 * **Wiederkehrend** (Lebensbereich › Gruppe › **Seite**) und auf **Kurz notiert**, das keine
 * Gruppe hat (Lebensbereich › **Kurz notiert**).
 *
 * - **Leer** steht er fest dort: Überschrift und Untertitel fallen weg (24.09.2026).
 * - **Mit Inhalt** erst, wenn die Überschrift oben aus dem Bild gerollt ist — wie auf
 *   Projekt-Seiten (#273), mit derselben Entscheidung (`breadcrumbTitleFor`) und demselben Rand.
 *
 * Nur der aktive Tab setzt ihn: Die Breadcrumb gehört dem Fenster, und inaktive Tabs bleiben im
 * TabStack eingehängt — ihre Überschrift meldete sich dort als „nicht sichtbar“.
 */
export function usePageBreadcrumb({ title, isActive, loaded, isEmpty, titleRef, scrollerRef }: UsePageBreadcrumbParams): void {
  const { setTitle } = useBreadcrumbTitle()
  useEffect(() => {
    if (!isActive || !loaded) return

    if (isEmpty) {
      setTitle(title)
      return () => setTitle(null)
    }

    const titleEl = titleRef.current
    const scroller = scrollerRef.current
    if (!titleEl || !scroller) return
    const observer = new IntersectionObserver(
      ([entry]) => setTitle(breadcrumbTitleFor(entry, title)),
      { root: scroller, threshold: 0, rootMargin: '-16px 0px 0px 0px' },
    )
    observer.observe(titleEl)
    return () => {
      observer.disconnect()
      setTitle(null)
    }
    // Die Refs hängen, sobald `loaded` und `isEmpty` stehen — beide sind Abhängigkeiten.
  }, [isActive, loaded, isEmpty, title, titleRef, scrollerRef, setTitle])
}
